import { useState } from 'react'
import type { CharacterWithStats } from '@shared/types'
import { hitDieFor } from '@shared/rules/rest'
import { ApiError, api } from '@/lib/api'
import { Button, Panel, SectionHeading } from '@/components/ui'
import { cn } from '@/lib/cn'

export function RestPanel({
  character, canEdit, onUpdated, onError,
}: {
  character: CharacterWithStats
  canEdit: boolean
  onUpdated: (next: CharacterWithStats) => void
  onError: (message: string | null) => void
}) {
  const [busy, setBusy] = useState<string | null>(null)
  const [flash, setFlash] = useState<string[] | null>(null)

  const remaining = Math.max(0, character.hitDiceTotal - character.hitDiceSpent)
  const die = hitDieFor(character.class)
  const hurt = character.hpCurrent < character.hpMax

  async function rest(type: 'short' | 'long') {
    setBusy(type)
    onError(null)
    try {
      const res = await api.rest(character.id, type)
      onUpdated(res.character)
      setFlash(res.summary)
      setTimeout(() => setFlash(null), 6000)
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not take that rest')
    } finally {
      setBusy(null)
    }
  }

  async function spendHitDie() {
    setBusy('die')
    onError(null)
    try {
      const res = await api.spendHitDie(character.id)
      onUpdated(res.character)
      setFlash([`Rolled ${res.rolled} on a d${res.die} and healed ${res.healed}`])
      setTimeout(() => setFlash(null), 6000)
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not spend a hit die')
    } finally {
      setBusy(null)
    }
  }

  if (!canEdit) return null

  return (
    <Panel className="space-y-3 p-4">
      <SectionHeading
        action={
          <span className="font-mono text-xs text-ink-400">
            {remaining}/{character.hitDiceTotal} <span className="text-ink-500">d{die}</span>
          </span>
        }
      >
        Rest &amp; recovery
      </SectionHeading>

      <div className="flex flex-wrap gap-2">
        <Button size="sm" busy={busy === 'die'} disabled={remaining === 0 || !hurt}
          onClick={spendHitDie}
          title={remaining === 0 ? 'No hit dice left' : !hurt ? 'Already at full health' : `Roll 1d${die} + Constitution`}>
          Spend a hit die
        </Button>
        <Button size="sm" busy={busy === 'short'} onClick={() => rest('short')}>Short rest</Button>
        <Button size="sm" variant="primary" busy={busy === 'long'} onClick={() => rest('long')}>Long rest</Button>
      </div>

      {flash && (
        <ul className={cn('space-y-0.5 rounded-xl border border-moss-500/30 bg-moss-900/30 p-2.5 text-[13px] text-moss-500')}>
          {flash.map((line) => <li key={line}>{line}</li>)}
        </ul>
      )}
    </Panel>
  )
}
